import { Injectable, OnDestroy } from "@angular/core";
import { Select } from "@ngxs/store";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { filter, map, mergeMap, takeUntil } from "rxjs/operators";
import { isDefined } from "src/utils/isDefined/isDefined";
import { AppState } from "../app.state";
import { TripSemantics } from "../components/wizard/wizards/create-rule/define-duty/define-duty.component";
import { DisplayedCategory, DisplayedZone, Division, EXCLUDED_ZONES } from "../models/mir-rule.model";
import { GlobalApiCallsService } from "./global-api-calls.service";

@Injectable({
	providedIn: "root",
})
export class ZonesService implements OnDestroy {
	private destroy$ = new Subject<void>();

	constructor(private globalApiService: GlobalApiCallsService) {
		let _id;
		this.selectedCompanyId$
			.pipe(
				filter(isDefined),
				mergeMap(id => {
					_id = id;
					return this.globalApiService.getZoneCount({ companyId: _id }).pipe(
						map(count => {
							this.zoneCount$.next(count);
							return count;
						}),
					);
				}),
				mergeMap(_ => {
					if (!_id) {
						return [];
					}
					return this.globalApiService.getZoneCategories(_id);
				}),
				takeUntil(this.destroy$),
			)
			.subscribe(cats => {
				cats.forEach(cat => {
					cat.divisions.forEach(div => {
						this._setMaps(this._categoriesStart, { ...cat } as DisplayedCategory, div);
						this._setMaps(this._categoriesEnd, { ...cat } as DisplayedCategory, div);
						this._setMaps(this._categoriesExcluded, { ...cat } as DisplayedCategory, div);
					});
				});

				this.categoriesStart$.next(this._categoriesStart);
				this.categoriesEnd$.next(this._categoriesEnd);
				this.categoriesExcluded$.next(this._categoriesExcluded);
				this.categoryMaps = {
					[TripSemantics.tripStart]: {
						map: this._categoriesStart,
						obs: this.categoriesStart$,
					},
					[TripSemantics.tripEnd]: {
						map: this._categoriesEnd,
						obs: this.categoriesEnd$,
					},
					[EXCLUDED_ZONES]: {
						map: this._categoriesExcluded,
						obs: this.categoriesExcluded$,
					},
				};
			});
	}
	@Select(AppState.getSelectedCompanyId) selectedCompanyId$: Observable<string>;

	private _categoriesStart: Map<string, DisplayedCategory[]> = new Map();
	private _categoriesEnd: Map<string, DisplayedCategory[]> = new Map();
	private _categoriesExcluded: Map<string, DisplayedCategory[]> = new Map();

	categoriesStart$: BehaviorSubject<Map<string, DisplayedCategory[]>> = new BehaviorSubject(null);
	categoriesEnd$: BehaviorSubject<Map<string, DisplayedCategory[]>> = new BehaviorSubject(null);
	categoriesExcluded$: BehaviorSubject<Map<string, DisplayedCategory[]>> = new BehaviorSubject(null);

	zoneSearchCache = new Map();

	selectedZonesStart$: BehaviorSubject<DisplayedZone[]> = new BehaviorSubject(null);

	selectedZonesEnd$: BehaviorSubject<DisplayedZone[]> = new BehaviorSubject(null);

	selectedZonesExcluded$: BehaviorSubject<DisplayedZone[]> = new BehaviorSubject(null);

	zoneCount$: BehaviorSubject<number> = new BehaviorSubject(null);

	divisionId$: BehaviorSubject<string> = new BehaviorSubject(null);

	confirmDivisionChange$ = new Subject();

	categoryMaps = {};

	previouslySelectedDiv$: BehaviorSubject<Division> = new BehaviorSubject(null);

	hasOneDivision$: BehaviorSubject<boolean> = new BehaviorSubject(null);

	cancelClicked$ = new Subject();
	applyClicked$ = new Subject();

	closeDialog$ = new Subject();

	zonesOnApplyClicked: { [key: string]: DisplayedZone[] } = {};

	private _setMaps(map: Map<string, DisplayedCategory[]>, category: DisplayedCategory, divisionId: string) {
		if (!map.get(divisionId)) {
			map.set(divisionId, []);
			map.get(divisionId).push(category);
		} else {
			map.get(divisionId).push(category);
		}
	}

	setCheckmarks(checked: boolean) {
		const cat = this.categoryMaps;
		Object.keys(cat).forEach(tripType => {
			const map = cat[tripType].map;
			const keys = [...map.keys()];

			keys.forEach(key => {
				map.get(key).forEach(c => {
					if (c?.zones?.length) {
						c.zones.forEach(z => {
							z.checked = checked;
						});
					}
				});
			});
		});
	}

	reset(resetDivision = true) {
		this.setCheckmarks(false);
		// Set selected zones to empty arrays instead of null to ensure component subscriptions trigger
		this.selectedZonesStart$.next([]);
		this.selectedZonesEnd$.next([]);
		this.selectedZonesExcluded$.next([]);
		// Clear the apply clicked cache
		// this.zonesOnApplyClicked = [];
		if (resetDivision) {
			this.divisionId$.next(null);
		}
	}
	setZonesInCache(divisionId: string, categoryId: string, zones: DisplayedZone[], tripType: TripSemantics | string) {
		if (this.categoryMaps[tripType].map.has(divisionId)) {
			const categories = this.categoryMaps[tripType].map.get(divisionId);
			const cat = categories.find(c => c.id === categoryId);

			if (cat) {
				cat.zones = zones;
			}
		} else {
			throw new Error(`${divisionId} not found in cache.`);
		}
	}

	getZonesByCategoryId(categoryId: string, companyId: string) {
		return this.globalApiService.getCompanyZones({
			companyId,
			categoryId,
			sort: "name",
		});
	}

	getZonesBySearch(companyId: string, search: string) {
		return this.globalApiService.getCompanyZones({
			companyId,
			q: `name:${search}`,
			sort: "name",
		});
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
